import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

// ROUTES
import { WebsmsRoutes } from '../../routes/WebsmsRoutes';

// REQUESTS
import useComposeRequest from '../../requests/ComposeRequest';
import useSettingsRequest from '../../requests/SettingsRequest';
import useTimezoneRequest from '../../requests/TimezoneRequest';

// MESSAGES
import CreditsMessagesCounts from '../../components/common/CreditsMessagesCounts';
import SettingsFormattedMessages from '../../messages/websms/SettingsFormattedMessages';
import WebsmsFormattedMessages from '../../messages/websms/WebsmsFormattedMessages';

// COMPONENTS
import LanguagePicker from '../../components/common/LanguagePicker';
import SectionWrapper from '../../components/common/SectionWrapper';
import SideNote from '../../components/common/SideNote';
import Layout from '../../components/layouts/Layout';
import Loader from '../../components/common/Loader';
import PasswordRequirements from '../../components/common/PasswordRequirements';

// UTILS
import { Build, Cancel } from '@mui/icons-material';
import MfaChoiceDialog from '../../components/websms/settings/MfaChoiceDialog';
import MfaVerificationDialog from '../../components/websms/settings/MfaVerificationDialog';
import {
  getCharacterCount,
  getMessageMaxLength,
  withinCharacterLimit,
} from '../../utils/characterCount';
import { validatePassword, validatePhoneNumber } from '../../utils/validate';
import ConfirmModal from '../../components/common/modals/ConfirmModal';

const Settings = ({ customer, user, errorHandler, navigate }) => {
  const [deliveryReceipts, setDeliveryReceipts] = useState(false);
  const [deliveryNumber, setDeliveryNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validPassword, setValidPassword] = useState(true);
  const [validConfirmPassword, setValidConfirmPassword] = useState(true);
  const [languageId, setLanguageId] = useState('');
  const [timeOffset, setTimeOffset] = useState(null);
  const [signature, setSignature] = useState('');
  const [forwardReplies, setForwardReplies] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [timezones, setTimezones] = useState([]);
  const [error, setError] = useState(null);
  const [creditsTotal, setCreditsTotal] = useState(null);
  const [messagesTotal, setMessagesTotal] = useState(null);

  const [mfaValue, setMfaValue] = useState('');
  const [mfaOption, setMfaOption] = useState('Disabled');
  const [mfaOptionCode, setMfaOptionCode] = useState(null);
  const [showMfaChoiceDialog, setShowMfaChoiceDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showMfaVerificationDialog, setShowMfaVerificationDialog] =
    useState(false);
  const [mfaAuthenticatorCode, setMfaAuthenticatorCode] = useState(null);

  const { getTimezoneOffset } = useTimezoneRequest();
  const { getSettings, updateSettings, disableMfa } = useSettingsRequest();
  const { getNoSmsSentForUser } = useComposeRequest();

  useEffect(() => {
    if (customer) {
      getUserSettings();
    }
    console.log(user);
  }, [customer]);

  useEffect(() => {
    if (!customer) return;

    const fetchData = async () => {
      const { count, creditCount, messageCount } = await getNoSmsSentForUser(
        errorHandler
      );

      setCreditsTotal(creditCount);
      setMessagesTotal(messageCount);
    };

    fetchData();
  }, [customer, location.state, errorHandler]);

  // Formats the credits total and message total into strings if they weren't already.
  if (creditsTotal === null) setCreditsTotal('Loading...');
  else if (typeof creditsTotal === 'number')
    setCreditsTotal(
      new Intl.NumberFormat('en-US', {
        style: 'decimal',
        minimumFractionDigits: 1,
      }).format(creditsTotal)
    );

  if (!messagesTotal) setMessagesTotal('Loading...');
  else if (typeof messagesTotal === 'number')
    setMessagesTotal(new Intl.NumberFormat('en-US').format(messagesTotal));

  const getUserSettings = async () => {
    const settingsData = await getSettings(errorHandler);
    setDeliveryNumber(settingsData.phone_number || '');
    setLanguageId(settingsData.language || '');
    setTimeOffset(settingsData.time_offset || '+000010050010050');
    setSignature(settingsData.signature || '');
    setForwardReplies(settingsData.replies_type);
    setDeliveryReceipts(settingsData.receipt_flag);
    setTimezones(settingsData.timezones || '');
    setMfaValue(settingsData.mfa_option);
    setMfaOptionLabel(settingsData.mfa_option);

    setLoaded(true);
  };

  const setMfaOptionLabel = (option) => {
    switch (option) {
      case 'S':
        setMfaOption('SMS');
        break;
      case 'E':
        setMfaOption('Email');
        break;
      case 'A':
        setMfaOption('Authenticator App');
        break;
      default:
        setMfaOption('Disabled');
    }
  };

  // Counts character for message (and signature if 'allowSignature' is true)
  const {
    standardCharacterOnly,
    unicodeCharacterOnly,
    characterCount,
    isUnicodeMessage,
  } = getCharacterCount(signature);

  const messageWithinLength = withinCharacterLimit(signature, isUnicodeMessage);
  const messageMaxLength = getMessageMaxLength(isUnicodeMessage);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoaded(false);
    setError(null);

    const newSettings = {
      receipt_flag: deliveryReceipts,
      message_originator: deliveryNumber,
    };

    if (deliveryNumber.trim() !== '' && !validatePhoneNumber(deliveryNumber)) {
      setError(SettingsFormattedMessages.invalidPhoneText);
      setLoaded(true);
      return;
    }

    if (password && !validatePassword(password)) {
      setError(SettingsFormattedMessages.passwordNotSecure);
      setLoaded(true);
      return;
    }

    if (password !== confirmPassword) {
      setError(SettingsFormattedMessages.passwordMisMatch);
      setLoaded(true);
      return;
    }

    newSettings.phone_number = deliveryNumber;
    newSettings.password = password;
    newSettings.password_check = confirmPassword;
    newSettings.replies_type = forwardReplies;
    newSettings.language = languageId;
    newSettings.time_offset = timeOffset;
    newSettings.timezoneString = newSettings.time_offset;
    newSettings.signature = signature;
    newSettings.mfa_option = mfaOptionCode;

    await updateSettings(newSettings, errorHandler);

    const time = await getTimezoneOffset(errorHandler);
    localStorage.setItem('offset', time.timezone_offset);
    localStorage.setItem('timeString', time.timezone_name);
    localStorage.setItem('bias', time.timezone_bias);
    localStorage.setItem('timeShort', time.time_short);
    localStorage.setItem('timeIdentifier', time.timezone_identifier);

    navigate('/websms/compose');
    setLoaded(true);
  };

  const handleMfaOptionChange = (option, authenticatorCode) => {
    setShowMfaChoiceDialog(false);

    if (option) {
      setMfaOptionCode(option);
      setMfaAuthenticatorCode(authenticatorCode);
      setShowMfaVerificationDialog(true);
    }
  };

  const handleMfaVerificationChange = () => {
    setShowMfaVerificationDialog(false);
    setLoaded(false);
    getUserSettings();
  };

  const handleDisableMfa = async () => {
    await disableMfa(errorHandler);
    setShowConfirmDialog(false);
    setLoaded(false);
    getUserSettings();
  };

  return (
    <Layout routes={WebsmsRoutes} title={WebsmsFormattedMessages.settingsTitle}>
      {!loaded ? (
        <Loader />
      ) : (
        <Grid container sx={{ justifyContent: 'space-between' }}>
          <Grid size={{ xs: 12, lg: 9 }}>
            <Grid container sx={{ gap: 2 }}>
              <Grid size={12}>
                <SectionWrapper
                  title={SettingsFormattedMessages.settingsTitle}
                  icon="settings"
                >
                  <Stack sx={{ gap: 2 }}>
                    <Stack
                      sx={{ flexDirection: { lg: 'row' }, gap: 3, flex: 1 }}
                    >
                      <Stack
                        sx={{
                          flexDirection: { md: 'row' },
                          columnGap: 2,
                          alignItems: { md: 'center' },
                          width: { lg: '75%' },
                        }}
                      >
                        <InputLabel sx={{ minWidth: 160 }}>
                          {SettingsFormattedMessages.deliveryReceiptsText}:
                        </InputLabel>

                        <FormControlLabel
                          control={
                            <Checkbox
                              color="secondary"
                              checked={deliveryReceipts}
                              onChange={(e) =>
                                setDeliveryReceipts(e.target.checked)
                              }
                            />
                          }
                          label={deliveryReceipts ? 'Enabled' : 'Disabled'}
                          sx={{ margin: 0 }}
                        />
                      </Stack>
                    </Stack>

                    <Stack
                      sx={{
                        flexDirection: { md: 'row' },
                        columnGap: 2,
                        alignItems: { md: 'center' },
                        width: { lg: '75%' },
                      }}
                    >
                      <InputLabel sx={{ minWidth: 160 }}>
                        {SettingsFormattedMessages.mobileText}:
                      </InputLabel>

                      <TextField
                        size="small"
                        value={deliveryNumber}
                        onChange={(e) => setDeliveryNumber(e.target.value)}
                        fullWidth
                        variant="outlined"
                      />
                    </Stack>

                    <Stack
                      sx={{
                        marginLeft: { md: 26 },
                        flexDirection: { xl: 'row' },
                        marginBottom: 4,
                        width: { lg: '75%' },
                      }}
                    >
                      <Typography>Forward replies to:</Typography>

                      <RadioGroup
                        value={forwardReplies}
                        onChange={(e) => setForwardReplies(e.target.value)}
                        sx={{ marginLeft: { xl: 4 } }}
                      >
                        <FormControlLabel
                          name="forwardReplies"
                          type="radio"
                          value="S"
                          control={
                            <Radio
                              sx={{ paddingY: 0 }}
                              color="secondary"
                              disabled={deliveryNumber === ''}
                            />
                          }
                          label={SettingsFormattedMessages.forwardMobileText}
                          checked={
                            deliveryNumber !== '' && forwardReplies === 'S'
                          }
                        />

                        <FormControlLabel
                          name="forwardReplies"
                          value="E"
                          type="radio"
                          control={
                            <Radio sx={{ paddingY: 0 }} color="secondary" />
                          }
                          label={SettingsFormattedMessages.forwardEmailText}
                          checked={
                            deliveryNumber === '' || forwardReplies === 'E'
                          }
                        />
                      </RadioGroup>
                    </Stack>

                    <Stack
                      sx={{
                        flexDirection: { md: 'row' },
                        columnGap: 2,
                        alignItems: { md: 'center' },
                        width: { lg: '75%' },
                      }}
                    >
                      <InputLabel sx={{ minWidth: 160 }}>Language:</InputLabel>

                      <LanguagePicker
                        selectedLanguageId={languageId}
                        onChange={setLanguageId}
                        errorHandler={errorHandler}
                      />
                    </Stack>

                    <Stack
                      sx={{
                        flexDirection: { md: 'row' },
                        columnGap: 2,
                        alignItems: { md: 'center' },
                        marginBottom: 4,
                        width: { lg: '75%' },
                      }}
                    >
                      <InputLabel sx={{ minWidth: 160 }}>Time Zone:</InputLabel>

                      <Box sx={{ overflow: 'hidden', width: '100%' }}>
                        <Select
                          value={timeOffset}
                          size="small"
                          fullWidth
                          onChange={(e) => setTimeOffset(e.target.value)}
                        >
                          {timezones.map((tz) => (
                            <MenuItem
                              key={tz.timezone_offset}
                              value={tz.timezone_offset}
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {tz.timezone_name}
                            </MenuItem>
                          ))}
                        </Select>
                        <Typography fontSize={10} sx={{ paddingLeft: 4 }}>
                          CompleteSMS Server: Timezone is (GMT+00:00) UTC
                          Standard Time
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack sx={{ flexDirection: { lg: 'row' }, flex: 1 }}>
                      <Stack sx={{ width: { lg: '75%' }, gap: 2 }}>
                        <Stack
                          sx={{
                            flexDirection: { md: 'row' },
                            columnGap: 2,
                            alignItems: { md: 'center' },
                          }}
                        >
                          <InputLabel sx={{ minWidth: 160 }}>
                            {SettingsFormattedMessages.password}:
                          </InputLabel>

                          <Tooltip title="Password must be strong">
                            <TextField
                              size={'small'}
                              type="password"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              error={!validPassword}
                              fullWidth
                            />
                          </Tooltip>
                        </Stack>

                        <Stack
                          sx={{
                            flexDirection: { md: 'row' },
                            columnGap: 2,
                            alignItems: { md: 'center' },
                          }}
                        >
                          <InputLabel sx={{ minWidth: 160 }}>
                            {SettingsFormattedMessages.confirmPassword}:
                          </InputLabel>

                          <TextField
                            size={'small'}
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            error={!validConfirmPassword}
                            fullWidth
                          />
                        </Stack>
                      </Stack>
                      <Stack
                        sx={{ width: { lg: '25%' }, paddingLeft: { lg: 2 } }}
                      >
                        <PasswordRequirements />
                      </Stack>
                    </Stack>

                    <Stack
                      sx={{
                        flexDirection: { lg: 'row' },
                        columnGap: 2,
                        alignItems: { lg: 'center' },
                        width: { lg: '75%' },
                      }}
                    >
                      <InputLabel sx={{ minWidth: 160 }}>
                        {SettingsFormattedMessages.mfa}:
                      </InputLabel>

                      <Stack
                        sx={{
                          flexDirection: 'row',
                          gap: 2,
                          alignItems: 'center',
                        }}
                      >
                        <Typography sx={{ marginBottom: { lg: 0 } }}>
                          {mfaOption}
                        </Typography>

                        {mfaOption === 'Disabled' ? (
                          <Tooltip title="Setup">
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => setShowMfaChoiceDialog(true)}
                            >
                              <Build fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <>
                            <Tooltip title="Change">
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => setShowMfaChoiceDialog(true)}
                              >
                                <Build fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Disable">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setShowConfirmDialog(true)}
                              >
                                <Cancel fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                    </Stack>
                  </Stack>
                </SectionWrapper>
              </Grid>

              <Grid size={{ xs: 12, lg: 8.5 }}>
                <SectionWrapper title={'Signature'} icon="compose">
                  <Stack sx={{ gap: 1 }}>
                    <TextField
                      size={'small'}
                      label={SettingsFormattedMessages.signatureText}
                      value={signature}
                      onChange={(e) => setSignature(e.target.value)}
                      fullWidth
                      multiline
                      rows={4}
                    />

                    <Typography fontSize={12}>
                      {SettingsFormattedMessages.signatureCharacterMessage}
                    </Typography>

                    <Typography
                      fontSize={12}
                      sx={{
                        color: !messageWithinLength ? 'error.main' : 'inherit',
                        fontWeight: !messageWithinLength ? 700 : 400,
                      }}
                    >
                      <strong>{`${characterCount}/${messageMaxLength} Character(s)`}</strong>
                      {` = ${standardCharacterOnly} Standard Character(s)
                        ${
                          unicodeCharacterOnly > 0
                            ? ' + ' +
                              unicodeCharacterOnly +
                              ' Unicode Character(s)'
                            : ''
                        }`}
                    </Typography>
                  </Stack>
                </SectionWrapper>
              </Grid>
            </Grid>

            {error && <Alert severity="error">{error}</Alert>}

            <Grid marginTop={4}>
              <Stack sx={{ flexDirection: 'row', gap: 2 }}>
                <Button
                  color="secondary"
                  onClick={() => navigate('/websms/compose')}
                  sx={{
                    textTransform: 'none',
                    textDecoration: 'underline',
                    fontWeight: 400,
                  }}
                >
                  {SettingsFormattedMessages.cancelText}
                </Button>

                <Button
                  type="submit"
                  onClick={handleSubmit}
                  variant="contained"
                  color="secondary"
                >
                  {SettingsFormattedMessages.saveText}
                </Button>
              </Stack>
            </Grid>
          </Grid>
          <Grid size={{ xs: 12, lg: 2.5 }}>
            {/*Comment the side note until it has been decided how to be managed*/}
            {/* <SideNote
              message="Notes could be added that are associated with each block. I&rsquo;d want them to disappear on mobile so they couldn&rsquo;t be anything mandatory for submission."
              sx={{
                display: { xs: 'none', md: 'block' },
                marginTop: { md: 4, lg: 0 },
              }}
            /> */}
            <CreditsMessagesCounts
              creditsTotal={creditsTotal}
              messagesTotal={messagesTotal}
              sx={{
                marginTop: { xs: 5, lg: 10 },
              }}
            />
          </Grid>
        </Grid>
      )}

      <MfaChoiceDialog
        show={showMfaChoiceDialog}
        option={mfaValue}
        onChange={handleMfaOptionChange}
        errorHandler={errorHandler}
      />

      <MfaVerificationDialog
        show={showMfaVerificationDialog}
        option={mfaOptionCode}
        username={user.user_email}
        authenticatorCode={mfaAuthenticatorCode}
        onHide={handleMfaVerificationChange}
        errorHandler={errorHandler}
      />

      <ConfirmModal
        show={showConfirmDialog}
        title="Disable MFA"
        message="Are you sure you want to disable Multi-Factor Authentication?"
        onHide={() => setShowConfirmDialog(false)}
        onConfirm={handleDisableMfa}
      />
    </Layout>
  );
};

export default Settings;
